import { State, appendValue, appendOrMoveToIndexWith, moveTo } from '@btime/state-monad'
import { ascend, assoc, compose, curry, dissoc, filter, groupBy, map, path, prop, propEq, sortWith, values } from 'ramda'
import { formatAddressToString, shouldRenderReply, checkReplyValidation, findParentReply } from '@btime/helpers'
import React from 'react'

const formatBlockQuestions = questions => {
  const getChildQuestions = (parentReference, allQuestions) => {
    return allQuestions.filter(propEq('parentReference', parentReference)).reduce((finalQuestions, parentQuestion) => {
      return [...finalQuestions, parentQuestion, ...getChildQuestions(parentQuestion.reference, allQuestions)]
    }, [])
  }

  return questions.map(question => {
    if (question.type.name !== 'block') return question
    return { ...question, blockQuestions: getChildQuestions(question.reference, questions) }
  })
}

const formatBlockReplies = allReplies => formattedReplies => {
  return formattedReplies
    .map(reply => {
      if (reply.question.type.name !== 'block') return reply
      const questionIds = reply.question.blockQuestions?.map(prop('id'))
      return {
        ...reply,
        value: compose(
          values,
          groupBy(prop('blockIndex')),
          filter(reply => questionIds.includes(reply.questionId))
        )(allReplies || [])
      }
    })
    .filter(reply => [-1, null, undefined].includes(reply.blockIndex) && reply.blockIndex)
}

const formatReplies = questions => {
  return questions.map(question => {
    const reply = {
      questionId: question.id,
      question: question,
      type: question.type?.name,
      value: question.defaultValue !== undefined && question.defaultValue !== null ? question.defaultValue : undefined,
      documents: []
    }

    return { ...reply }
  }, [])
}

const formatCategoriesAndReplies = questions => {
  return questions.reduce((categories, question) => {
    const reply = {
      questionId: question.id,
      question: question,
      type: question.type?.name,
      value: question.defaultValue !== undefined && question.defaultValue !== null ? question.defaultValue : undefined,
      documents: []
    }

    const category = {
      ...question.category,
      ordination: question.categoryOrdination,
      data: []
    }

    return State.of(categories)
      .map(appendOrMoveToIndexWith(propEq('id', question.category.id), category))
      .map(moveTo('data'))
      .map(appendValue(reply))
      .fold()
  }, [])
}

const replaceExistingReplies = curry((savedReplies, reply) => {
  if (!savedReplies || !savedReplies.length) return reply

  const question = reply.question
  const savedReply = savedReplies.find(propEq('questionId', question.id)) || {}

  return {
    ...reply,
    ...savedReply,
    question: reply.question
  }
})

const setParentName = replies =>
  replies.map(reply => assoc('parentName', findParentReply(reply, replies)?.question?.name, reply))

const setIfIsParent = replies =>
  replies.map(reply => assoc('isParent', !!findParentReply(reply, replies)?.question?.name, reply))

const setShouldRenderReplies = replies =>
  replies.map(reply => assoc('shouldRender', shouldRenderReply(reply, replies), reply))

const setInitialValidation = reply => assoc('validations', checkReplyValidation(reply), reply)

const AUTOFILL_SOURCE_USER = 'user'
const AUTOFILL_SOURCE_PLACE = 'place'

export const isAddressLikeObject = value =>
  value != null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  ('search' in value || 'address' in value || 'country' in value || 'city' in value)

export const normalizeAutofillDisplayValue = value => {
  if (value == null) return ''
  if (isAddressLikeObject(value)) return formatAddressToString(value) || value?.search || ''
  if (typeof value === 'object') return ''
  return String(value)
}

export const normalizeAutofillValueForInput = (value, questionType) => {
  if (value == null) return undefined
  const isAddressType = questionType === 'address' || questionType === 'location'
  if (isAddressType && isAddressLikeObject(value)) return value
  if (typeof value === 'object') return normalizeAutofillDisplayValue(value)
  return String(value)
}

export const hasAutofillValue = value =>
  value != null &&
  (typeof value === 'object' ? isAddressLikeObject(value) : value !== '')

export const hasReplyValue = reply => {
  const v = reply?.value
  if (v === undefined || v === null) return false
  if (typeof v === 'string') return v.trim() !== ''
  if (typeof v === 'object') {
    if (Array.isArray(v)) return v.length > 0
    if (isAddressLikeObject(v)) return !!formatAddressToString(v)?.trim() || !!v?.search?.trim()
    return Object.keys(v).length > 0
  }
  return true
}

const parseQuestionData = data => {
  if (data == null) return {}
  if (typeof data === 'object') return data
  if (typeof data !== 'string') return {}
  try {
    return JSON.parse(data) || {}
  } catch {
    return {}
  }
}

const normalizeFieldId = val => {
  if (val == null) return null
  if (typeof val === 'object' && 'id' in val) return val.id
  return val
}

const normalizeSource = src => {
  if (src == null) return null
  const raw = typeof src === 'object' && src !== null && 'id' in src ? src.id : src
  const s = raw != null ? String(raw).toLowerCase() : null
  return s || null
}

export const getAutofillConfig = question => {
  const rawData = question?.data
  const data = parseQuestionData(rawData)
  const autofill = data.autofill || question?.autofill
  if (!autofill) return null
  const source = normalizeSource(autofill.source?.id ?? autofill.source)
  const fieldId = normalizeFieldId(autofill.fieldId ?? autofill.field_id)
  if (!source || fieldId == null) return null
  return { source, fieldId }
}

const sameFieldId = (fv, fieldId) =>
  String(fv.fieldId ?? fv.field?.id ?? '') === String(fieldId)

export const getFieldValueByFieldId = (fieldValues, fieldId) => {
  if (!fieldValues?.length || fieldId == null) return undefined
  const fv = fieldValues.find(fv => sameFieldId(fv, fieldId))
  return fv?.value
}

export const getAutofillValue = (question, autofillContext) => {
  const config = getAutofillConfig(question)
  if (!config || !autofillContext) return undefined

  const { source, fieldId } = config

  if (source === AUTOFILL_SOURCE_USER && autofillContext.userFieldValues) {
    return getFieldValueByFieldId(autofillContext.userFieldValues, fieldId)
  }

  if (source === AUTOFILL_SOURCE_PLACE && autofillContext.placeFieldValues) {
    return getFieldValueByFieldId(autofillContext.placeFieldValues, fieldId)
  }

  return undefined
}

const applyAutofillPlaceholder = autofillContext => reply => {
  if (!autofillContext || reply.question?.type?.name === 'block') return reply
  if (hasReplyValue(reply)) return dissoc('autofillPlaceholder', reply)
  const value = getAutofillValue(reply.question, autofillContext)
  if (value === undefined) return reply
  return assoc('autofillPlaceholder', value, reply)
}

const flattenBlockReplies = replies => {
  const allReplies = replies.map(reply => {
    return {
      ...reply,
      blockIndex: -1
    }
  })
  return allReplies
}

export const useFormattedRepliesFormData = (questions, replies, autofillContext) => {
  return React.useMemo(() => {
    const formattedReplies = compose(
      map(category => ({
        ...category,
        data: compose(
          setParentName,
          setShouldRenderReplies,
          formatBlockReplies(replies),
          flattenBlockReplies,
          map(
            compose(
              setInitialValidation,
              replaceExistingReplies(replies),
              applyAutofillPlaceholder(autofillContext)
            )
          ),
          sortWith([ascend(path(['question', 'ordination']))])
        )(category.data)
      })),
      sortWith([ascend(prop('ordination'))]),
      formatCategoriesAndReplies,
      formatBlockQuestions
    )(questions)
    return assoc('categories', formattedReplies, {})
  }, [replies, questions, autofillContext])
}

export const useFormatBlockReplies = (questions, replies) => {
  return React.useMemo(() => {
    return compose(
      compose(
        setIfIsParent,
        setShouldRenderReplies,
        map(
          compose(
            setInitialValidation,
            replaceExistingReplies(replies)
          )
        ),
        sortWith([ascend(path(['question', 'ordination']))])
      ),
      sortWith([ascend(prop('ordination'))]),
      formatReplies
    )(questions)
  }, [replies, questions])
}
